// Decides whether a TanStack Query/Mutation error is worth reporting to Sentry.
//
// Expected, handled client errors are noise, not bugs — reporting them buries the real
// failures (500s, network errors) we actually need to jump on. This matters most on
// shared surfaces like the Bulletin Board, where optimistic-concurrency **409**s happen
// routinely (two people editing the same note/column → "someone changed this, refetch").
//
// Skipped (not reported):
//   401 — auth; handled by the auth interceptor / re-login.
//   403 — permission; expected, not a bug (the query handler already treated it this way).
//   409 — optimistic-concurrency / handled-per-mutation; expected and recovered from.
//   502/503/504 — see GATEWAY_UNAVAILABLE_STATUSES below.
//   cancellations — see isCancellation() below.
//   timeouts & network drops without response — see isTimeoutError() and isAxiosNetworkError() below.
//   expected client statuses declared via query/mutation `meta: { expectedStatuses: [...] }`.
//   404 on `/timeclock/status` (`timeclock-status` query) — expected business state for unlinked Kimai users.
//
// Reported: everything else — 500 server errors, unexpected client exceptions (TypeError, malformed data),
// and unexpected 4xx like 400 (malformed request → likely a real frontend bug) and unexpected 404s.
import { LawLibraryError } from '../api/lawLibraryErrors';

const EXPECTED_CLIENT_ERROR_STATUSES = new Set([401, 403, 409]);

// Valentine auto-deploys on every merge to master — roughly 14 restarts a day. For the
// ~16s the service is down, EVERY in-flight and polled request from EVERY open browser
// comes back 502 through nginx, and each one used to become its own Sentry event.
//
// These statuses mean "the backend is not there right now", which is a condition, not a
// per-request bug. The circuit breaker in api/http.ts already reports that condition once
// per 30s window, so the per-request events were pure duplication of a signal we already
// have — and they scale with deploy frequency and headcount, not with anything wrong.
//
// A genuine sustained outage still surfaces: the breaker keeps firing for as long as it
// lasts. A real application fault is a **500**, which is deliberately NOT in this set.
const GATEWAY_UNAVAILABLE_STATUSES = new Set([502, 503, 504]);

/** Shared by api/http.ts so both reporting paths agree on what "backend is down" means. */
export function isGatewayUnavailableStatus(status: number | undefined): boolean {
  return status !== undefined && GATEWAY_UNAVAILABLE_STATUSES.has(status);
}

/**
 * Whether an error is a cancellation rather than a failure.
 *
 * The circuit breaker rejects queued requests with a synthetic axios `CanceledError`
 * while it is open ("backend appears unavailable", "Valentine is restarting"). Those
 * carry no `response`, so the "no status → real failure" rule below used to report the
 * app's OWN backpressure signal to Sentry as an error.
 *
 * Matched on the error's `name`/`code` — never on its message, so a real error that
 * merely talks about cancelling something is still reported.
 */
function isCancellation(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const { name, code } = error as { name?: unknown; code?: unknown };
  return name === 'CanceledError' || name === 'AbortError' || code === 'ERR_CANCELED';
}

/**
 * Whether an error represents a client timeout (no response received before deadline).
 *
 * Background polling queries and slow connections routinely time out when client laptops
 * sleep, tabs throttle in the background, or Wi-Fi blips. These are transient network/client
 * conditions rather than application bugs.
 */
export function isTimeoutError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const candidate = error as {
    name?: unknown;
    code?: unknown;
    message?: unknown;
    response?: unknown;
  };
  if (candidate.response !== undefined) return false;

  if (candidate.name === 'TimeoutError') return true;
  if (candidate.code === 'ECONNABORTED' || candidate.code === 'ETIMEDOUT') return true;

  if (typeof candidate.message === 'string') {
    return /timeout.*exceeded|timeout of \d+ms exceeded|\btimed out\b/i.test(candidate.message);
  }
  return false;
}

/**
 * Whether an error is an Axios network drop without a response.
 *
 * Network drops (Wi-Fi disconnect, laptop sleep, tab unload) produce an AxiosError
 * without an HTTP response. As established in VALENTINE-Z0, these are client conditions,
 * not application bugs, and sustained unreachability is aggregated by the circuit breaker.
 * Non-Axios errors (e.g. standard TypeError or generic Error) remain reported.
 */
export function isAxiosNetworkError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const candidate = error as {
    isAxiosError?: unknown;
    name?: unknown;
    code?: unknown;
    message?: unknown;
    response?: unknown;
  };
  if (candidate.response !== undefined) return false;

  const isAxios = candidate.isAxiosError === true || candidate.name === 'AxiosError';
  if (!isAxios) return false;

  if (
    candidate.code === 'ERR_NETWORK' ||
    candidate.code === 'ERR_INTERNET_DISCONNECTED' ||
    candidate.code === 'ECONNRESET' ||
    candidate.code === 'ECONNREFUSED'
  ) {
    return true;
  }

  if (typeof candidate.message === 'string' && candidate.message.toLowerCase() === 'network error') {
    return true;
  }

  return false;
}

export interface SentryFilterMeta {
  expectedStatuses?: readonly number[] | number[] | Set<number> | number;
  allow404?: boolean;
  [key: string]: unknown;
}

export interface SentryFilterContext {
  queryKey?: unknown;
  meta?: SentryFilterMeta;
}

function isTimeclockStatusError(error: unknown, queryKey?: unknown): boolean {
  if (Array.isArray(queryKey)) {
    if (queryKey.some((k) => k === 'timeclock-status' || (typeof k === 'string' && k.includes('timeclock-status')))) {
      return true;
    }
  } else if (typeof queryKey === 'string' && queryKey.includes('timeclock-status')) {
    return true;
  }

  const url = (error as { config?: { url?: string } } | null)?.config?.url;
  if (typeof url === 'string' && url.includes('/timeclock/status')) {
    return true;
  }

  const detail = (error as { response?: { data?: { detail?: unknown } } } | null)?.response?.data?.detail;
  if (typeof detail === 'string' && detail.toLowerCase().includes('no time clock account found')) {
    return true;
  }

  return false;
}

export function shouldReportToSentry(
  error: unknown,
  queryKeyOrContext?: unknown,
  meta?: SentryFilterMeta,
): boolean {
  // #1964: the Law Library's errors are expected answers the backend already logged; reporting
  // one would also carry its query key into the event (queryClient.ts).
  if (error instanceof LawLibraryError) return false;
  if (isCancellation(error)) return false;
  if (isTimeoutError(error)) return false;
  if (isAxiosNetworkError(error)) return false;
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  if (status === undefined) return true; // non-network / thrown runtime error without HTTP status → real failure
  if (GATEWAY_UNAVAILABLE_STATUSES.has(status)) return false;
  if (EXPECTED_CLIENT_ERROR_STATUSES.has(status)) return false;

  let resolvedMeta = meta;
  let resolvedKey = queryKeyOrContext;
  if (
    queryKeyOrContext &&
    typeof queryKeyOrContext === 'object' &&
    !Array.isArray(queryKeyOrContext) &&
    ('meta' in queryKeyOrContext || 'queryKey' in queryKeyOrContext)
  ) {
    const ctx = queryKeyOrContext as SentryFilterContext;
    resolvedMeta = ctx.meta ?? resolvedMeta;
    resolvedKey = ctx.queryKey ?? resolvedKey;
  }

  if (resolvedMeta) {
    const expected = resolvedMeta.expectedStatuses;
    if (Array.isArray(expected) && expected.includes(status)) {
      return false;
    }
    if (expected instanceof Set && expected.has(status)) {
      return false;
    }
    if (typeof expected === 'number' && expected === status) {
      return false;
    }
    if (resolvedMeta.allow404 && status === 404) {
      return false;
    }
  }

  // 404 on timeclock status indicates the user has no linked Kimai account
  // (e.g. attorneys, admins who do not punch a clock).
  // The frontend handles this cleanly ({ kind: 'not-configured', reason: 'unlinked' }).
  if (status === 404 && isTimeclockStatusError(error, resolvedKey)) {
    return false;
  }

  return true;
}

